import * as React from "react";
import {Button, Icon, Segment} from "semantic-ui-react";
import AceEditor from "react-ace";

import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-xml";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-monokai";

import "./OriginalFileViewer.css";
import {XlsxViewer} from "../../../fileViewer/XlsxViewer";

interface ViewableFile {
    file: File;
    url?: string;
    text?: string;
    arrayBuffer?: ArrayBuffer;
}

type FileCategory = "pdf" | "xlsx" | "text";

function categorizeFile(fileName: string): FileCategory {
    const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
    if (ext === "pdf") return "pdf";
    if (ext === "xlsx" || ext === "xls") return "xlsx";
    return "text";
}

function getAceMode(fileName: string): string {
    const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
    const map: Record<string, string> = {
        json: "json",
        jsonmail: "json",
        xml: "xml",
        html: "html",
        htm: "html",
        edi: "text",
        eml: "text",
        csv: "text",
        txt: "text",
        fortras: "text",
        docx: "text",
    };
    return map[ext] ?? "text";
}

export interface OriginalFileViewerProps {
    files: File[];
}

export function OriginalFileViewer({files}: OriginalFileViewerProps) {
    const [viewables, setViewables] = React.useState<ViewableFile[]>([]);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [visible, setVisible] = React.useState(false);

    React.useEffect(() => {
        if (!files.length) {
            setViewables([]);
            setVisible(false);
            return;
        }

        const next: ViewableFile[] = files.map((f) => ({file: f}));
        const urls: string[] = [];

        next.forEach((v, i) => {
            const category = categorizeFile(v.file.name);

            if (category === "pdf") {
                const url = URL.createObjectURL(v.file);
                urls.push(url);
                next[i] = {...v, url};
            } else if (category === "xlsx") {
                v.file.arrayBuffer().then((buf) => {
                    setViewables((prev) => {
                        const updated = [...prev];
                        if (updated[i]) {
                            updated[i] = {...updated[i], arrayBuffer: buf};
                        }
                        return updated;
                    });
                });
            } else {
                v.file.text().then((text) => {
                    setViewables((prev) => {
                        const updated = [...prev];
                        if (updated[i]) {
                            updated[i] = {...updated[i], text};
                        }
                        return updated;
                    });
                });
            }
        });

        setViewables(next);
        setActiveIndex(0);
        setVisible(false);

        return () => {
            urls.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [files]);

    if (!viewables.length) return null;

    const current = viewables[activeIndex];
    if (!current) return null;

    const category = categorizeFile(current.file.name);

    return (
        <div className="original-file-viewer">
            <Button
                className="original-file-viewer__toggle"
                size="mini"
                icon
                labelPosition="left"
                toggle
                active={visible}
                onClick={() => setVisible((v) => !v)}
            >
                <Icon name={visible ? "eye slash" : "eye"}/>
                {visible ? "Hide Original" : "Show Original"}
            </Button>

            {visible && (
                <Segment className="original-file-viewer__content">
                    {viewables.length > 1 && (
                        <div className="original-file-viewer__tabs">
                            {viewables.map((v, i) => (
                                <Button
                                    key={v.file.name + i}
                                    size="mini"
                                    primary={i === activeIndex}
                                    onClick={() => setActiveIndex(i)}
                                >
                                    {v.file.name}
                                </Button>
                            ))}
                        </div>
                    )}

                    {category === "pdf" && (
                        <iframe
                            className="original-file-viewer__pdf"
                            src={current.url}
                            title={current.file.name}
                        />
                    )}

                    {category === "xlsx" && (
                        current.arrayBuffer ? (
                            <div className="original-file-viewer__xlsx">
                                <XlsxViewer fileBuffer={current.arrayBuffer}/>
                            </div>
                        ) : (
                            <div>Loading spreadsheet…</div>
                        )
                    )}

                    {category === "text" && (
                        <AceEditor
                            mode={getAceMode(current.file.name)}
                            theme="monokai"
                            name="originalFileAceEditor"
                            value={current.text ?? "Loading…"}
                            readOnly
                            width="100%"
                            height="40vh"
                            fontSize={13}
                            showPrintMargin={false}
                            editorProps={{$blockScrolling: true}}
                        />
                    )}
                </Segment>
            )}
        </div>
    );
}